import AdminLayout from "@/components/layout-admin";
import { useBuildings, useCreateBuilding, useUpdateBuilding, useDeleteBuilding } from "@/hooks/use-resources";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Building2, MapPin, Pencil, Trash2, Home } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import type { Building } from "@shared/schema";

export default function AdminBuildings() {
  const { data: buildings, isLoading } = useBuildings();
  const createMutation = useCreateBuilding();
  const updateMutation = useUpdateBuilding();
  const deleteMutation = useDeleteBuilding();
  const [createOpen, setCreateOpen] = useState(false);
  const [editBuilding, setEditBuilding] = useState<Building | null>(null);
  const { toast } = useToast();

  const [newBuilding, setNewBuilding] = useState({ name: "", address: "", description: "" });
  const [editForm, setEditForm] = useState({ name: "", address: "", description: "" });

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync(newBuilding);
      setCreateOpen(false);
      setNewBuilding({ name: "", address: "", description: "" });
      toast({ title: "Building added" });
    } catch {
      toast({ title: "Failed to add building", variant: "destructive" });
    }
  };

  const handleEdit = (b: Building) => {
    setEditBuilding(b);
    setEditForm({ name: b.name, address: b.address, description: b.description || "" });
  };

  const handleUpdate = async () => {
    if (!editBuilding) return;
    try {
      await updateMutation.mutateAsync({ id: editBuilding.id, data: editForm });
      toast({ title: "Building updated" });
      setEditBuilding(null);
    } catch {
      toast({ title: "Failed to update building", variant: "destructive" });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast({ title: "Building deleted" });
    } catch {
      toast({ title: "Failed to delete building", variant: "destructive" });
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Buildings</h1>
          <p className="text-muted-foreground mt-2">Manage your properties and locations.</p>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-building">
              <Plus className="w-4 h-4 mr-2" /> Add Building
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Building</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input
                  placeholder="e.g. Ocean View Tower"
                  value={newBuilding.name}
                  onChange={e => setNewBuilding({ ...newBuilding, name: e.target.value })}
                  data-testid="input-building-name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Address</label>
                <Input
                  placeholder="123 Ocean Drive, Miami"
                  value={newBuilding.address}
                  onChange={e => setNewBuilding({ ...newBuilding, address: e.target.value })}
                  data-testid="input-building-address"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  placeholder="Luxury condos with beachfront..."
                  value={newBuilding.description}
                  onChange={e => setNewBuilding({ ...newBuilding, description: e.target.value })}
                  data-testid="input-building-description"
                />
              </div>
              <Button className="w-full" onClick={handleCreate} disabled={createMutation.isPending} data-testid="button-submit-building">
                {createMutation.isPending ? "Creating..." : "Create Building"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">Loading buildings...</div>
      )}

      {!isLoading && (!buildings || buildings.length === 0) && (
        <div className="text-center py-12" data-testid="text-empty">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No buildings yet. Add your first building to get started.</p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {buildings?.map((b) => (
          <Card key={b.id} data-testid={`card-building-${b.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold font-display" data-testid={`text-building-name-${b.id}`}>{b.name}</h3>
                  <div className="flex items-center text-sm text-muted-foreground mt-1 gap-1 flex-wrap">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span data-testid={`text-building-address-${b.id}`}>{b.address}</span>
                  </div>
                  {b.description && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2" data-testid={`text-building-description-${b.id}`}>{b.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 mt-4 pt-4 border-t justify-end">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleEdit(b)}
                  data-testid={`button-edit-building-${b.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid={`button-delete-building-${b.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Building</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{b.name}"? This will also remove all associated apartments, sections, and data. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(b.id)}
                        data-testid="button-confirm-delete"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!editBuilding} onOpenChange={(open) => { if (!open) setEditBuilding(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Building</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={editForm.name}
                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="input-edit-building-name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Address</label>
              <Input
                value={editForm.address}
                onChange={e => setEditForm({ ...editForm, address: e.target.value })}
                data-testid="input-edit-building-address"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={editForm.description}
                onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                data-testid="input-edit-building-description"
              />
            </div>
            <Button className="w-full" onClick={handleUpdate} disabled={updateMutation.isPending} data-testid="button-update-building">
              {updateMutation.isPending ? "Updating..." : "Update Building"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
